// src/hooks/cotizacion/useCotizacionForm.ts
import { useState, useCallback, useEffect, useRef } from 'react'
import type { CotizacionData, Departamento, Ciudad } from '../../types/cotizacionFormTypes'
import { COTIZACION_STATES } from '../../assets/data/arrays'
import { synchronizeCustomerData } from '../../utils/cotizacionFormUtils'
import { parseClienteNetoFlag } from '../../utils/clienteNetoFlag'

function mergeClienteNetoFromMetaCot (initialData: CotizacionData): CotizacionData {
  const prev = initialData.extraDetails || {}
  const md = initialData.meta_data as unknown
  const metaArr = Array.isArray(md) ? md : []
  const entry = metaArr.find((m: { key?: string; value?: unknown }) => m.key === 'cliente_neto')
  if (entry == null || entry.value === undefined || entry.value === null) {
    return initialData
  }
  const clienteNeto = parseClienteNetoFlag(entry.value)
  if (prev.cliente_neto === clienteNeto) return initialData
  return {
    ...initialData,
    extraDetails: { ...prev, cliente_neto: clienteNeto }
  }
}

function normalizeClienteNetoExtraCot (initialData: CotizacionData): CotizacionData {
  const prev = initialData.extraDetails || {}
  const raw = (prev as { cliente_neto?: unknown }).cliente_neto
  if (raw === undefined || raw === null) return initialData

  const clienteNeto = parseClienteNetoFlag(raw)
  if (prev.cliente_neto === clienteNeto) return initialData
  return {
    ...initialData,
    extraDetails: { ...prev, cliente_neto: clienteNeto }
  }
}

interface UseCotizacionFormProps {
  initialData?: CotizacionData
  mode: 'view' | 'edit' | 'create'
  departamentos: Departamento[]
  ciudades: Ciudad[]
  onFormChange?: (data: CotizacionData) => void
}

export function useCotizacionForm({
  initialData,
  mode,
  departamentos: deptos,
  ciudades: cities,
  onFormChange
}: UseCotizacionFormProps) {
  const userLS = JSON.parse(localStorage.getItem('user') || '{}')

  const [formData, setFormData] = useState<CotizacionData>(() => {
    if (initialData) {
      return normalizeClienteNetoExtraCot(
        mergeClienteNetoFromMetaCot(initialData as CotizacionData)
      )
    }
    // Si es modo create, el estado por defecto es el primero del array
    return { 
      status: COTIZACION_STATES[0]?.value || '',
      agente: userLS.id || '',
      customer: {
        first_name: '',
        last_name: '',
        email: '',
        documento: '',
        tipo_documento: ''
      },
      billingAddress: {
        address_1: '',
        city: '',
        state: '',
        phone: ''
      },
      shippingAddress: {
        address_1: '',
        city: '',
        state: '',
        phone: ''
      },
      extraDetails: {
        tipo_lead: '',
        tipo_cliente: '',
        marca: '',
        banco: '',
        transportadora: '',
        conocer: '',
        id_pago: '',
        forma_pago: '',
        payment_method: '',
        customer_note: '',
        transaction_id: '',
        destino_cotizacion: '',
        store_name: '',
        costo_envio: '0',
        ciudad_origen: '',
        ciudad_destino: '',
        expiration_limit: 0,
        cliente_neto: false
      },
      line_items: [],
      meta_data: [
        { key: 'costo_envio', value: '0' }
      ]
    } as CotizacionData
  })

  // Función auxiliar para actualizar meta_data de manera eficiente
  const updateMetaData = useCallback((key: string, value: string) => {
    setFormData(prev => {
      const meta_data = Array.isArray(prev.meta_data) ? [...prev.meta_data] : []
      const idx = meta_data.findIndex((m: any) => m.key === key)
      
      if (idx >= 0) {
        meta_data[idx] = { ...meta_data[idx], value }
      } else {
        meta_data.push({ key, value })
      }
      
      return { ...prev, meta_data }
    })
  }, [])

  // Ref para almacenar el callback y evitar dependencias cambiantes
  const onFormChangeRef = useRef(onFormChange)
  const prevFormDataRef = useRef<CotizacionData | null>(null)
  const isInitialMountRef = useRef(true)

  // Actualizar ref cuando cambie el callback
  useEffect(() => {
    onFormChangeRef.current = onFormChange
  }, [onFormChange])

  // Efecto para llamar a onFormChange después del render
  useEffect(() => {
    // No llamar en el montaje inicial
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      prevFormDataRef.current = formData
      return
    }

    // Solo llamar si formData realmente cambió
    if (prevFormDataRef.current !== formData) {
      prevFormDataRef.current = formData
      
      // Usar setTimeout para diferir la llamada después del render
      setTimeout(() => {
        onFormChangeRef.current?.(formData)
      }, 0)
    }
  }, [formData])

  // Función para manejar cambios en los campos del formulario optimizada
  const handleInputChange = useCallback((
    section: keyof CotizacionData,
    field: string,
    value: any
  ) => {
    setFormData(prev => {
      const newData = { ...prev }
      
      // Manejar campos de nivel superior
      if (section === 'status' || section === 'guia' || section === 'agente' || section === 'total' || section === 'store_name') {
        newData[section] = value
      } else {
        // Manejar campos anidados
        if (!newData[section]) {
          newData[section] = {} as any
        }
        
        // Lógica especial para departamentos y ciudades
        if (field === 'state' && (section === 'billingAddress' || section === 'shippingAddress')) {
          const departamentoSeleccionado = deptos.find(d => d.Nombre === value)
          if (departamentoSeleccionado) {
            const codigo = departamentoSeleccionado.Codigo
            const codigoStr = typeof codigo === 'number' ? codigo.toString() : codigo
            ;(newData[section] as any)[field] = codigoStr
            ;(newData[section] as any)['city'] = ''
          } else {
            (newData[section] as any)[field] = value
          }
        } else if (field === 'city' && (section === 'billingAddress' || section === 'shippingAddress')) {
          const ciudadSeleccionada = cities.find(c => c.ciudad === value)
          if (ciudadSeleccionada) {
            (newData[section] as any)[field] = ciudadSeleccionada.codigo
          } else {
            (newData[section] as any)[field] = value
          }
        } else {
          (newData[section] as any)[field] = value
        }
        
        // Sincronizar datos de cliente y direcciones
        synchronizeCustomerData(newData, section, field, value)
      }
      
      // NO llamar onFormChange aquí - se llamará en el useEffect
      return newData
    })
  }, [deptos, cities])

  return {
    formData,
    setFormData,
    handleInputChange,
    updateMetaData
  }
}

